import {
	captureException,
	captureMessage,
	init,
	setContext,
	setTags,
} from '@sentry/nextjs'
import axios from 'axios'
import axiosRetry from 'axios-retry'
import { Currencies } from 'currencies-map'
import { addDays, addWeeks, format } from 'date-fns'
import { startCase } from 'lodash'
import {
	DATE_FORMAT_QUERY,
	DEFAULT_ALLOCATION,
	DEFAULT_CHECK_IN_DATE,
	DEFAULT_CHECK_OUT_DATE,
	DEFAULT_STAR_RATING,
	FALLBACK_IP_ADDRESS,
	HOTEL_DEFAULT_CHECK_IN_WEEKS,
	HOTEL_DEFAULT_STAY_DAYS,
	INIT_DATA_STORAGE_KEY,
	ROOM_GUESTS_SEPARATOR,
	userAgents,
} from '../constants'
import {
	DestPagesProps,
	HotelsListResponse,
	SearchInPlaceRequestParams,
	SearchInPlaceRequestResponse,
	SearchPlaceType,
	SORT_BY,
} from '../types'
import {
	checkDate,
	determineSearchPlaceFromPath,
	formatNumber,
	hasValidDates,
	isBotUA,
	isLandmark,
	needsHotelDateFallback,
	parseSearchParamsToLower,
	randomizeUserAgents,
} from '../utils'

const DEFAULT_PAGE = 0

const axiosInstance = axios.create()

axiosRetry(axiosInstance, {
	retries: 4,
	retryDelay: (retryCount) => {
		console.log(`retry attempt (fetchSearchInPlace): ${retryCount}`)
		return 2000
	},
	retryCondition(error) {
		if (error.status && error.status <= 404) {
			return false
		}

		return true
	},
})

const getStarRating = (s: string | undefined) => {
	if (s === undefined || s === 'undefined' || s === '') {
		return DEFAULT_STAR_RATING
	}
	const validRatings = s
		.split('|')
		.filter((rating) => ['0', '1', '2', '3', '4', '5'].includes(rating))
	return validRatings.length > 0 ? validRatings.join('|') : DEFAULT_STAR_RATING
}

const getUserRating = (rating?: string): string | null => {
	if (!rating || rating[0] === '0' || rating === 'undefined') {
		return null
	}
	return rating
}

export async function fetchSearchInPlace(
	params: SearchInPlaceRequestParams
): Promise<SearchInPlaceRequestResponse | null> {
	init({
		dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
		debug: false,
		tracesSampleRate: 0,
		replaysOnErrorSampleRate: 0,
		replaysSessionSampleRate: 0,
		environment: process.env.NEXT_PUBLIC_SENTRY_ENV,
	})

	const url = `${process.env.NEXT_PUBLIC_KAYAK_URL}/search-in-place`
	const bareParams = JSON.stringify(params)

	setContext('Request Params', {
		searchParams: bareParams,
		url,
	})

	setTags(JSON.parse(bareParams))

	try {
		const response = await axiosInstance(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			data: JSON.stringify(params),
		})

		if (!response.status || response.status < 200 || response.status >= 300) {
			setContext('fetchSearchInPlace response with error', {
				response,
			})

			console.error(`HTTP error! status: ${response.status}`)
			captureException(`Error in fetchSearchInPlace: HTTP ${response.status}`)
			return null
		}

		const data = await response.data

		if (data?.complete && data.hotels?.length === 0) {
			captureMessage('Hotels data not found')
			setContext('Response Data', data)
		}

		return data as SearchInPlaceRequestResponse
	} catch (e: any) {
		setContext('catch fetchSearchInPlace', {
			error: e,
		})

		console.log('Error Message:', e.message)
		console.log('Error in fetchSearchInPlace:', e)
		captureException(`Error in fetchSearchInPlace: ${e.message}`)
	}

	return null
}

export async function getHotelsData(
	params: DestPagesProps,
	skipFilters?: boolean,
	excludedHotels?: string[]
): Promise<HotelsListResponse> {
	const sessionID = crypto.randomUUID()
	let userAgent = params.searchParams.userAgent || navigator.userAgent

	const [c, r, ci, h, p] = params?.params?.dest

	const pathname = window.location.pathname
	const hasHotelInPath = pathname.includes('/hotel/')

	const country = !!c ? startCase(decodeURIComponent(c)) : ''
	const region = !!r ? startCase(decodeURIComponent(r)) : ''
	const city = !!ci ? startCase(decodeURIComponent(ci)) : ''
	const hotel = !!h ? decodeURIComponent(h) : undefined
	const place = p ? startCase(decodeURIComponent(p)) : ''

	const {
		room,
		page,
		s: stars,
		gr: guestRating,
		p: property,
		f: facilities,
		ap: allowedPartners,
		t: themes,
		h: hotelChain,
		mn: minPrice,
		mx: maxPrice,
		by,
		dir,
	} = params.searchParams

	let allocations = [DEFAULT_ALLOCATION]

	if (userAgent && isBotUA(userAgent)) {
		userAgent = randomizeUserAgents(userAgents)
	}

	const { type, name } = determineSearchPlaceFromPath(
		[country, region, city, hotel as string, place],
		hasHotelInPath
	)

	if (room) {
		let roomParam = room

		if (!Array.isArray(room)) {
			roomParam = [room]
		}

		allocations = roomParam.map((room) => {
			const [adults, children = ''] = room.split(ROOM_GUESTS_SEPARATOR)
			const childAges = children
				? children.split(',').map((age) => Number(age))
				: []

			return {
				adults: parseInt(adults) || 1,
				childAge: childAges,
			}
		})
	}

	let result: SearchInPlaceRequestResponse | null = null

	const searchParams = parseSearchParamsToLower(params.searchParams)
	const onlyComplete = Boolean(searchParams?.onlycomplete)

	const currency = searchParams?.currency
	const timezone =
		searchParams?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
	const latlon = searchParams?.latlon
	const utcOffset = searchParams?.['utcoffsetminutes']
		? Number(searchParams['utcoffsetminutes'])
		: new Date().getTimezoneOffset() * -1

	// made case insensitive in case of human error
	const hotelID = searchParams?.['hotelid']
	const placeID = searchParams?.['placeid']

	const isHotel = type === SearchPlaceType.HOTEL

	let customEntityKey =
		isHotel && !isLandmark(hotel as string)
			? hotelID && `khotel:${hotelID}`
			: placeID && `kplace:${placeID}`

	if (isLandmark(hotel as string) && latlon) {
		customEntityKey = `latlon:${latlon}`
	}

	let userIP = FALLBACK_IP_ADDRESS
	const storedData = localStorage.getItem(INIT_DATA_STORAGE_KEY)

	if (storedData) {
		try {
			const parsedData = JSON.parse(storedData)
			userIP = parsedData.userIP || FALLBACK_IP_ADDRESS
		} catch (error) {
			console.error('Error parsing stored user data:', error)
		}
	}

	let checkIn = params.searchParams['check-in-date']
	let checkOut = params.searchParams['check-out-date']

	if (type === SearchPlaceType.HOTEL && needsHotelDateFallback(checkIn, checkOut)) {
		const checkInDate = addWeeks(new Date(), HOTEL_DEFAULT_CHECK_IN_WEEKS)
		checkIn = format(checkInDate, DATE_FORMAT_QUERY)
		// Set checkout to check-in + default stay days
		checkOut = format(addDays(checkInDate, HOTEL_DEFAULT_STAY_DAYS), DATE_FORMAT_QUERY)
	}
	// For non-hotel searches or when dates are provided but invalid
	else if (!hasValidDates(checkIn, checkOut)) {
		checkIn = DEFAULT_CHECK_IN_DATE
		checkOut = DEFAULT_CHECK_OUT_DATE
	}
	try {
		result = await fetchSearchInPlace({
			userAgent,
			sessionID: searchParams?.['sessionid'] || sessionID,
			clientIP: userIP,
			allocations,
			language: params.params.lang,
			currency,
			checkIn,
			checkOut,
			timeZone: timezone,
			onlyComplete: onlyComplete || undefined,
			utcOffsetMinutes: utcOffset,
			place: {
				name,
				type,
				entityKey: customEntityKey || undefined,
				excludedHotels,
			},
			page: page ? parseInt(page) - 1 : DEFAULT_PAGE,

			// Update only for single hotel requests
			query: isHotel && !customEntityKey ? hotel : undefined,

			s: getStarRating(stars),
			...(getUserRating(guestRating) && { gr: getUserRating(guestRating) }),
			h: hotelChain,
			p: property,
			f: facilities,
			t: themes,
			mn: Number(minPrice) || undefined,
			mx: Number(maxPrice) || undefined,
			sort: {
				by: by || SORT_BY.POPULARITY,
				dir: dir,
			},
			ap: allowedPartners,
		})
	} catch (e) {
		console.log('Fetch hotels', e)
		captureMessage('Error in fetching hotels')
	}

	if (!result || result === null) {
		return {
			hotels: [],
			page: 0,
			pages: 0,
			totalResults: 0,
			complete: true,
			refreshInterval: 0,
			lowestTotalRate: 0,
			highestTotalRate: 0,
			lowestNightlyRate: 0,
			highestNightlyRate: 0,
			searchParams: {
				'check-in-date': params.searchParams['check-in-date'],
				'check-out-date': params.searchParams['check-out-date'],
				room: room,
			},
			filters: {
				facilities: {},
				propertyTypes: {},
				starRatings: {},
				guestRatings: {},
				hotelThemes: {},
			},
		}
	} else {
		const priceSymbol = Currencies.symbols.get(result.currencyCode) || ''

		return {
			hotels: result.hotels
				? result.hotels.map((hotel) => {
						return {
							title: hotel.name,
							image: hotel.image,
							translatedName: hotel.translatedName,
							largeImage: hotel.largeImage,
							location: hotel.address,
							stars: hotel.starRating,
							rating: hotel.guestRating,
							ratingText: hotel.guestRatingSentiment.split(',')[0],
							bestPriceOn: hotel.bestRate.provider.name,
							provider: hotel.bestRate.provider.name,
							numberOfReviews: hotel.numberOfReviews,
							distanceFromCenter: hotel.distance,
							propertyType: hotel.propertyType,
							price: `${priceSymbol}${formatNumber(
								Math.floor(+hotel.bestRate.nightlyRate)
							)}`,
							currency: result!.currencyCode,
							rawPrice: +hotel.bestRate.nightlyRate,
							lang: result!.languageCode,
							link: hotel.bestRate.bookURI,
							rawHotelData: hotel,
							rates: hotel.rates.reverse().map((rate) => ({
								...rate,
								providerName: rate.provider.name,
								providerLogo: rate.provider.logo,
								nightlyRate: `${priceSymbol}  ${formatNumber(
									Math.floor(+rate.nightlyRate)
								)}`,
								rawPrice: +rate.nightlyRate,
							})),
						}
				  })
				: [],
			page: result.page,
			pages: result.pages,
			totalResults: result.totalResults,
			complete: result.complete,
			lowestTotalRate: Math.floor(result.lowestTotalRate),
			highestTotalRate: Math.floor(result.highestTotalRate),
			lowestNightlyRate: Math.floor(result.lowestNightlyRate),
			highestNightlyRate: Math.floor(result.highestNightlyRate),
			refreshInterval: 500,
			searchParams: {
				'check-in-date': params.searchParams['check-in-date'],
				'check-out-date': params.searchParams['check-out-date'],
				room: room,
			},
			filters: result.filters,
		}
	}
}
